import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { Pause, Play, X } from 'lucide-react'
import { EYEBROW } from '../cc/tokens'
import { MbmtLiveMap } from './MbmtLiveMap'
import { positionAt, type MbmtActionable, type MbmtReplayClip } from './useMbmtApi'

const SPEEDS = [1, 4, 8, 16] as const

function clock(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })
}

function fmtHalt(sec: number): string {
  const h = Math.floor(sec / 3600)
  const m = Math.round((sec % 3600) / 60)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

/**
 * Breakdown evidence clip, as its own modal (Command Centre rework, alert-tile click fix).
 * Used to render inline in BusOpsShell.tsx's right-hand `<aside>`, auto-opened and
 * auto-overlaid onto the SHARED live map the instant a vehicle with an open breakdown was
 * selected -- the exact bug this fixes (selecting an alerted bus should only ever pan the
 * live map to its current state; the clip is now an explicit "View evidence" action).
 *
 * Promoted to the same `fixed inset-0 z-50` / `role="dialog" aria-modal="true"` modal
 * recipe MbmtDutyPlayback.tsx and MbmtTripAlerts.tsx's trip-detail window already use, with
 * its own embedded MbmtLiveMap (`vehicles={[]} fleetVehicles={[]} showFleetLegend={false}`)
 * instead of sharing BusOpsShell's live map instance, and its own play/pause/speed clock
 * (previously controlled by BusOpsShell).
 *
 * Plots `alerts` (that vehicle's day alerts, fetched by BusOpsShell.tsx's openEvidence() via
 * the same fetchMbmtVehicleDayAlerts() playDuty() already uses, filtered to this clip's own
 * window -- the breakdown-replay endpoint itself carries no alert data) on the embedded map
 * via the same `MbmtReplayOverlay.alerts` shape and marker layer MbmtDutyPlayback/
 * MbmtTripAlerts already use, plus a clickable "Alerts · N" timeline-dot strip (verbatim
 * pattern from MbmtDutyPlayback.tsx).
 */
export function MbmtEvidence({ clip, alerts, alertsTruncated, onClose }: {
  clip: MbmtReplayClip
  alerts: MbmtActionable[]
  /** True when BusOpsShell.tsx's openEvidence() genuinely skipped a day in the middle of
   *  this clip's own window (the onset day itself is always fetched, never dropped) --
   *  must be surfaced, not silently shown as a plain (undercounted) total
   *  (round-3/round-4 adversarial-review findings). */
  alertsTruncated?: boolean
  onClose: () => void
}): JSX.Element {
  const start = clip.started_ts
  const end = clip.ended_ts ?? Date.now()
  const span = Math.max(1, end - start)
  const xOf = (ts: number) => Math.max(0, Math.min(100, ((ts - start) / span) * 100))

  const [at, setAt] = useState(start)
  const [playing, setPlaying] = useState(false)
  const [speed, setSpeed] = useState(8)

  // Ref-held, like MbmtLiveMap.tsx's onSelectRef -- BusOpsShell re-renders this modal with
  // a brand-new `onClose` closure every second (its own `now` tick), and a plain `[onClose]`
  // dependency would tear this listener down and re-add it on every one of those renders.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  useEffect(() => {
    const esc = (e: KeyboardEvent): void => { if (e.key === 'Escape') onCloseRef.current() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [])

  // Depends on `clip` (stable identity across parent re-renders -- only a fresh
  // openEvidence() call replaces it) and NOT on `end`: for a still-open clip, `end =
  // clip.ended_ts ?? Date.now()` above is a fresh value on every render, and putting it in
  // this effect's own deps tore the interval down and rebuilt it on every parent re-render
  // (adversarial-review finding -- real wall-clock overhead a resolved clip, whose
  // `ended_ts` IS stable, never paid). `Date.now()` for a still-growing clip is read fresh
  // inside the tick itself instead, which is also more correct: the clip's own "now" bound
  // keeps advancing with real time while it plays, rather than being frozen at whichever
  // snapshot happened to be in scope when this effect last recreated.
  useEffect(() => {
    if (!playing) return
    const id = window.setInterval(() => {
      // setPlaying is called from inside setAt's own functional updater (round-3
      // adversarial-review finding flagged this as impure, matching
      // MbmtDutyPlayback.tsx/MbmtTripAlerts.tsx's identical existing pattern). A fix was
      // attempted and reverted: computing "reached end" into a local flag and calling
      // setPlaying(false) AFTER setAt() returns assumes the updater runs synchronously
      // within that same call -- it does not (React queues it and runs it when it
      // actually processes the update), so the flag was read before it was ever set,
      // and setPlaying(false) silently stopped firing at all (caught by
      // test_reaching_the_clips_end_clamps_the_playhead_and_stops_playing, which failed
      // under that version). The nested call here is the correct, working form.
      const currentEnd = clip.ended_ts ?? Date.now()
      setAt((prev) => {
        const next = prev + speed * 200
        if (next >= currentEnd) { setPlaying(false); return currentEnd }
        return next
      })
    }, 200)
    return () => window.clearInterval(id)
  }, [playing, speed, clip])

  const halt = useMemo(
    () => clip.events.find((e) => e.kind === 'halt'),
    [clip.events],
  )
  const geotags = useMemo(
    () => clip.events.filter((e) => e.kind === 'geotag'),
    [clip.events],
  )
  const haltSec = halt?.end_ts != null ? Math.max(0, (halt.end_ts - halt.ts) / 1000) : 0
  const inHalt = halt != null && at >= halt.ts && (halt.end_ts == null || at <= halt.end_ts)

  // The alerts that actually get a marker on the map (finite lat/lon) AND a real position
  // on the timeline strip below (a real `opened_ts`) -- used for BOTH the "Alerts · N"
  // count and the strip, not just the map overlay, so neither ever counts/draws a dot for
  // something the map itself silently dropped (round-3 adversarial-review finding: the
  // count used to be `alerts.length`, over the raw, unfiltered prop).
  const drawableAlerts = useMemo(
    () => alerts.filter((a) => a.opened_ts != null && Number.isFinite(a.lat) && Number.isFinite(a.lon)),
    [alerts],
  )

  const clipPos = useMemo(() => positionAt(clip.points, at), [clip.points, at])
  const overlay = useMemo(() => ({
    line: clip.points
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon))
      .map((p) => [p.lon as number, p.lat as number] as [number, number]),
    playhead: clipPos && Number.isFinite(clipPos.lat) && Number.isFinite(clipPos.lon)
      ? [clipPos.lon as number, clipPos.lat as number] as [number, number]
      : null,
    geotags: clip.events
      .filter((e) => e.kind === 'geotag' && Number.isFinite(e.lat) && Number.isFinite(e.lon))
      .map((e) => ({ lon: e.lon as number, lat: e.lat as number, name: e.name || e.stage_id || 'stage' })),
    alerts: drawableAlerts.map((a) => ({
      lon: a.lon as number,
      lat: a.lat as number,
      kind: a.category || 'alert',
      message: a.note,
    })),
  }), [clip, drawableAlerts, clipPos])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/25 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${clip.vehicle_id} evidence clip`}
        className="flex h-[min(88vh,760px)] w-full max-w-4xl flex-col overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-2xl"
      >
        <header className="flex shrink-0 flex-wrap items-start gap-3 border-b border-slate-200/80 px-5 py-3.5">
          <div className="min-w-0 flex-1">
            <p className={EYEBROW}>Evidence clip</p>
            <h2 className="mt-0.5 truncate text-[14px] font-semibold text-[#0F172A]">
              {clip.vehicle_id} · {clock(start)} → {clip.clip_complete ? clock(end) : clip.still_open ? 'live' : 'waiting for 2 geotags'}
            </h2>
            <p className="mt-0.5 text-[11px] text-[#64748B]">
              {clip.category || 'down'}{clip.severity ? ` · ${clip.severity}` : ''}
            </p>
          </div>
          <span className="rounded-full bg-[rgba(244,63,94,0.10)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#9F1239]">
            10 min before
          </span>
          {clip.still_open ? (
            <span className="rounded-full bg-[rgba(245,158,11,0.12)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#B45309]">
              growing
            </span>
          ) : clip.clip_complete ? (
            <span className="rounded-full bg-[rgba(16,185,129,0.12)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#047857]">
              2 geotags after resolve
            </span>
          ) : (
            <span className="rounded-full bg-[rgba(14,165,233,0.12)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#0369A1]">
              {clip.geotag_count} geotag{clip.geotag_count === 1 ? '' : 's'} after
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-slate-500 shadow-sm ring-1 ring-inset ring-slate-300 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="relative min-h-0 flex-1">
          <MbmtLiveMap
            vehicles={[]}
            fleetVehicles={[]}
            routes={[]}
            stages={[]}
            down={new Set()}
            selected={null}
            onSelect={() => {}}
            replay={overlay}
            showFleetLegend={false}
          />
        </div>

        <section className="shrink-0 border-t border-slate-200/80 p-3">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setPlaying((p) => !p)}
              className="flex h-8 items-center gap-1.5 rounded-full bg-[#0F172A] px-3 text-[12px] font-semibold text-white active:scale-[0.97]"
            >
              {playing ? <Pause size={12} /> : <Play size={12} />}
              {playing ? 'Pause' : 'Play'}
            </button>
            <span className="flex gap-0.5">
              {SPEEDS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSpeed(s)}
                  className={clsx(
                    'h-8 rounded-full px-2 font-mono text-[11px]',
                    speed === s ? 'bg-[#0F172A] text-white' : 'text-[#64748B] hover:bg-[#EEF2F7]',
                  )}
                >
                  {s}×
                </button>
              ))}
            </span>
          </div>

          <p className={clsx(EYEBROW, 'mb-1')}>Halt</p>
          <div className="relative mb-2 h-4 overflow-hidden rounded-full bg-[#EEF2F7]">
            {halt && (
              <span
                className="absolute top-0 h-full rounded-full bg-[rgba(244,63,94,0.45)]"
                style={{
                  left: `${xOf(halt.ts)}%`,
                  width: `${Math.max(1.5, xOf(halt.end_ts ?? end) - xOf(halt.ts))}%`,
                }}
                title={`Halt ${fmtHalt(haltSec)} · ${clock(halt.ts)}`}
              />
            )}
            <Playhead x={xOf(at)} />
          </div>

          <p className={clsx(EYEBROW, 'mb-1')}>Geotags · lake stages</p>
          <div className="relative mb-2 h-4 overflow-hidden rounded-full bg-[#EEF2F7]">
            {geotags.map((g, i) => (
              <button
                key={`${g.ts}-${i}`}
                type="button"
                title={`${clock(g.ts)} · ${g.name || g.stage_id || 'stage'}`}
                onClick={() => setAt(g.ts)}
                className="absolute top-0.5 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-white bg-[#2563EB]"
                style={{ left: `${xOf(g.ts)}%` }}
              />
            ))}
            <span
              className="absolute top-1/2 h-px w-full -translate-y-1/2 bg-[rgba(37,99,235,0.18)]"
              aria-hidden
            />
            <Playhead x={xOf(at)} />
          </div>

          <p className={clsx(EYEBROW, 'mb-1')}>
            Alerts · {drawableAlerts.length}
            {alertsTruncated && (
              <span className="ml-1.5 font-normal normal-case text-[#B45309]">
                (onset day + most recent days only -- this incident's own middle isn't shown)
              </span>
            )}
          </p>
          <div className="relative mb-2 h-4 overflow-hidden rounded-full bg-[#EEF2F7]">
            {drawableAlerts.map((a, i) => (
              <button
                key={`${a.id}-${i}`}
                type="button"
                title={`${clock(a.opened_ts as number)} · ${a.category || a.severity || 'alert'}`}
                onClick={() => setAt(a.opened_ts as number)}
                className={clsx(
                  'absolute top-0.5 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-white',
                  a.severity === 'CRITICAL' ? 'bg-[#F43F5E]' : 'bg-[#F59E0B]',
                )}
                style={{ left: `${xOf(a.opened_ts as number)}%` }}
              />
            ))}
            <span className="absolute top-1/2 h-px w-full -translate-y-1/2 bg-[rgba(245,158,11,0.18)]" aria-hidden />
            <Playhead x={xOf(at)} />
          </div>

          <input
            type="range"
            min={start}
            max={end}
            value={Math.max(start, Math.min(end, at))}
            onChange={(e) => setAt(Number(e.target.value))}
            className="w-full accent-[#0F172A]"
            aria-label="Evidence playhead"
          />

          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[12px] text-[#475569]">
            <span className="font-mono tabular-nums text-[#0F172A]">{clock(at)}</span>
            <span>
              {inHalt ? `Halted ${fmtHalt(haltSec)}` : 'Moving'}
              {' · '}{clip.geotag_count} geotags · {drawableAlerts.length} alerts · {clip.point_count} GPS
            </span>
          </div>
        </section>
      </div>
    </div>
  )
}

function Playhead({ x }: { x: number }): JSX.Element {
  return (
    <span
      className="pointer-events-none absolute top-0 z-10 h-full w-0.5 bg-[#0F172A]"
      style={{ left: `${x}%` }}
    />
  )
}
